import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'
import type { CredentialGroup } from '@/hooks/useDeviceProfileNetwork'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

export const FORM_CONFIG = formConfig

// The credential fields shown/required depend on the device's network type (from
// its Device Profile). LoRa keys, the cellular SIM field, and the WiFi fields are
// therefore all OPTIONAL at the schema level; the composer enforces the correct
// required set for the active network group (see Feature 4). Only the delivery
// date and serial number are always required.
export const ReceiveDeviceFormSchema = z.object({
  deviceDeliveryDate: z.string({
    message: "Received Date is required",
  }),
  deviceSerialNumber: z.string()
    .min(1, "Serial Number is required")
    .max(100, "Serial Number cannot exceed 100 characters"),
  // LoRa credential set
  deviceEUI: z.string().max(23, "Device EUI cannot exceed 23 characters").optional(),
  deviceJoinEUI: z.string().max(23, "Join EUI cannot exceed 23 characters").optional(),
  deviceAppKey: z.string().max(64, "APP Key cannot exceed 64 characters").optional(),
  deviceAddress: z.string().max(50, "Device Address cannot exceed 50 characters").optional(),
  netsKey: z.string().max(64, "NETS Key cannot exceed 64 characters").optional(),
  appsKey: z.string().max(64, "APPS Key cannot exceed 64 characters").optional(),
  // Cellular (3G/4G/5G) credential set
  simCardNumber: z.string().max(64, "SIM Card Number cannot exceed 64 characters").optional(),
  // WiFi credential set
  ssid: z.string().max(64, "SSID cannot exceed 64 characters").optional(),
  wifiPassword: z.string().max(128, "Password cannot exceed 128 characters").optional(),
})

export type ReceiveDeviceFormData = z.infer<typeof ReceiveDeviceFormSchema>

// Helper function to format EUI with colons (backend expects AA:BB:CC:DD:EE:FF:00:11 format)
function formatEUI(eui: string): string {
  // Remove any existing separators
  const cleanEUI = eui.replace(/[:-]/g, '');
  // Add colons between every 2 characters
  return cleanEUI.match(/.{1,2}/g)?.join(':') || eui;
}

export function transformToApiRequest(
  formData: ReceiveDeviceFormData,
  deviceNumber: number,
  deviceProcurementInfoNumber?: number,
  deviceKeyInfoNumber?: number,
  credentialGroup: CredentialGroup = 'lora'
) {
  // 1. Procurement info - ONLY delivery date and status
  const procurementInfo: any = {
    deviceNumber,
    deviceProcurementInfoNumber,
    deviceDeliveryDate: new Date(formData.deviceDeliveryDate).getTime() / 1000,
    deviceProcurementStatus: "received",
  }

  // 2. Device key info - the credential set depends on the device's network type.
  const deviceKeyInfo: any = {
    deviceNumber,
    deviceSerialNumber: formData.deviceSerialNumber,
    archiveDate: 0, // 0 means active record
  }

  if (credentialGroup === 'cellular') {
    // Cellular (3G/4G/5G): SIM card number. New backend field (hand-off pending).
    deviceKeyInfo.simCardNumber = formData.simCardNumber?.trim() || undefined
  } else if (credentialGroup === 'wifi') {
    // WiFi: SSID + password. New backend fields (hand-off pending).
    deviceKeyInfo.ssid = formData.ssid?.trim() || undefined
    deviceKeyInfo.wifiPassword = formData.wifiPassword?.trim() || undefined
  } else {
    // LoRa (default): the existing device-key fields.
    // Backend expects EUI format with colons: AA:BB:CC:DD:EE:FF:00:11
    deviceKeyInfo.deviceEUI = formatEUI(formData.deviceEUI ?? '')
    deviceKeyInfo.deviceJoinEUI = formatEUI(formData.deviceJoinEUI ?? '')
    // CRITICAL: Backend expects AES128Key object format!
    deviceKeyInfo.deviceAppKey = { rootKey: formData.deviceAppKey }
    // Backend model requires these fields but they're logically optional
    // Use "-" as placeholder for empty values (backend validation requires min(1) length)
    deviceKeyInfo.deviceAddress = formData.deviceAddress && formData.deviceAddress.trim() ? formData.deviceAddress : '-'
    deviceKeyInfo.deviceNETSKey = formData.netsKey && formData.netsKey.trim() ? formData.netsKey : '-'
    deviceKeyInfo.deviceAPPSKey = formData.appsKey && formData.appsKey.trim() ? formData.appsKey : '-'
  }

  // Add existing deviceKeyInfoNumber if updating
  if (deviceKeyInfoNumber) {
    deviceKeyInfo.deviceKeyInfoNumber = deviceKeyInfoNumber
  }

  return {
    procurementInfo,
    deviceKeyInfo,
  }
}
